// Made by Claude and Romi

import React, { useState, useEffect, useRef } from 'react';
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { ABLE_COLORS } from './constants';
import useTasks from './hooks/useTasks';
import useActivityLog from './hooks/useActivityLog';
import useInventory from './hooks/useInventory';
import useMealPlan from './hooks/useMealPlan';
import useFoodLog from './hooks/useFoodLog';
import useRecipes from './hooks/useRecipes';
import { useUIState, useFilterState, useUserState, useChatMessages } from './hooks/useAppState';
import ChatPanel from './components/ChatPanel';
import TaskCard from './components/TaskCard';
import FilterPanel from './components/FilterPanel';
import Modals from './components/Modals';
import PlanView from './components/PlanView';
import RoadmapView from './components/RoadmapView';
import SplashScreen from './components/SplashScreen';
import LandingPage from './components/LandingPage';
import ErrorBoundary from './components/ErrorBoundary';
import MemorySettings from './components/MemorySettings';
import ConsentDialog from './components/ConsentDialog';
import ReviewModal from './components/ReviewModal';
import Hub from './components/Hub';
import ViewHeader from './components/ViewHeader';
import { TimelineView } from './components/Timeline';
import { useToast } from './components/Toast';
import { getPendingFactsCount } from './utils/memoryApi';
import { getReviewQueueCount } from './utils/entityApi';
import { handleToolActions } from './utils/toolActionHandler';
import { Sidebar } from './components/Layout';
import { WidgetSettings, DomainSettings } from './components/Settings';
import useWidgetLayout from './hooks/useWidgetLayout';

// ============ MAIN COMPONENT ============

export default function AbleTaskManager() {
  // ============ AUTH (Kinde React SDK) ============

  const { isAuthenticated, isLoading, user, getToken } = useKindeAuth();

  // ============ TOAST NOTIFICATIONS ============

  const { showToast } = useToast();

  // ============ CONSOLIDATED STATE HOOKS ============

  const ui = useUIState();
  const filters = useFilterState();
  const { userState, setUserState } = useUserState();
  const { chatMessages, setChatMessages } = useChatMessages();

  // Review counts (kept here for refreshReviewCounts access)
  const [pendingFactsCount, setPendingFactsCount] = useState(0);
  const [pendingAliasesCount, setPendingAliasesCount] = useState(0);

  // ============ WIDGET LAYOUT (Sidebar + Domain Navigation) ============

  const widgetLayout = useWidgetLayout();
  const [currentDomain, setCurrentDomain] = useState('hub');
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);
  const [showDomainSettings, setShowDomainSettings] = useState(false);
  const isMobile = !ui.isDesktop;

  // ============ TASK HOOK ============

  const {
    tasks,
    filteredTasks,
    currentTask,
    focusIndex,
    contextOptions,
    isSyncing,
    syncError,
    addTask,
    addFullTask,
    markDone,
    markUndone,
    skipTask,
    moveTask,
    deleteTask,
    updateTask,
    toggleStar,
    clearAllTasks,
    importTasks
  } = useTasks(filters.capacity, ui.viewMode, filters.statusFilter, filters.showStarredOnly, getToken, isAuthenticated);

  // ============ ACTIVITY LOG ============

  const { entries: activityLogEntries, logTaskCompleted, logStateCheckin, clearLog: clearActivityLog } = useActivityLog();

  // ============ KITCHEN INVENTORY ============

  const {
    stockedItems,
    shoppingList,
    boughtItems,
    categories: inventoryCategories,
    addItem: addInventoryItem,
    addItems: addInventoryItems,
    updateItem: updateInventoryItem,
    removeItem: removeInventoryItem,
    markAsOut,
    markAsBought,
    undoBought,
    addToShoppingList
  } = useInventory('kitchen');

  // ============ MEAL PLAN ============

  const {
    meals: mealPlanMeals,
    weekStart: mealPlanWeekStart,
    isLoading: mealPlanLoading,
    isSaving: mealPlanSaving,
    addMeal,
    removeMeal,
    skipDay: skipMealDay,
    clearDay: clearMealDay,
    setWeekStart: setMealPlanWeekStart
  } = useMealPlan(getToken, isAuthenticated);

  // ============ FOOD LOG ============

  const {
    selectedDate: foodLogDate,
    entries: foodLogEntries,
    entriesByMeal,
    totals: foodLogTotals,
    isLoading: foodLogLoading,
    isSaving: foodLogSaving,
    isToday: foodLogIsToday,
    trackedMacros,
    dailyTargets,
    availableMacros,
    addEntry: addFoodEntry,
    deleteEntry: deleteFoodEntry,
    changeDate: changeFoodLogDate,
    goToToday: goToFoodLogToday,
    addTrackedMacro,
    removeTrackedMacro
  } = useFoodLog(getToken, isAuthenticated);

  // ============ RECIPES ============

  const {
    recipes,
    addRecipe,
    updateRecipe: updateUserRecipe,
    deleteRecipe: deleteUserRecipe,
    loadRecipes
  } = useRecipes(getToken, isAuthenticated);

  // ============ EFFECTS ============

  // Clean up /callback URL after auth redirect
  useEffect(() => {
    if (isAuthenticated && window.location.pathname === '/callback') {
      window.history.replaceState({}, '', '/');
    }
  }, [isAuthenticated]);

  // Check for consent on first authenticated login
  useEffect(() => {
    if (isAuthenticated && !localStorage.getItem('mabel-consent-given')) {
      ui.setShowConsentDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Fetch pending review counts on auth
  const refreshReviewCounts = async () => {
    if (!isAuthenticated) return;
    try {
      const token = await getToken();
      
      // Get facts count
      const factsData = await getPendingFactsCount(token);
      setPendingFactsCount(factsData.count || 0);
      
      // Get aliases count
      const aliasesData = await getReviewQueueCount(token);
      setPendingAliasesCount(aliasesData.count || 0);
    } catch (err) {
      console.error('Failed to fetch review counts:', err);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      refreshReviewCounts();
    }
    // refreshReviewCounts isn't memoised but we only want to fire on auth change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Surface task sync failures as a toast so silent data loss can't happen
  // unnoticed. Fires once per distinct error message.
  const lastShownSyncErrorRef = useRef(null);
  useEffect(() => {
    if (syncError && syncError !== lastShownSyncErrorRef.current) {
      lastShownSyncErrorRef.current = syncError;
      showToast(`Couldn't save tasks: ${syncError}`, { type: 'error' });
    }
    if (!syncError) {
      lastShownSyncErrorRef.current = null;
    }
  }, [syncError, showToast]);

  // ============ UI HANDLERS ============

  const handleAddTask = () => {
    addTask(ui.newTaskTitle);
    ui.setNewTaskTitle('');
  };

  const handleSkipTask = () => {
    skipTask();
    ui.setIsCardFlipped(false);
  };

  const handleMarkDone = (taskId) => {
    // Find the task to log it before marking done
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      logTaskCompleted(task);
      // Show celebration toast
      showToast('', { type: 'celebration' });
    }
    markDone(taskId);
    ui.setIsCardFlipped(false);
  };

  const handleMarkUndone = (taskId) => {
    markUndone(taskId);
  };

  const handleUpdateTask = (taskId, updates) => {
    updateTask(taskId, updates);
    ui.setEditingTask(null);
  };

  const handleDragStart = (e, taskId) => {
    e.dataTransfer.setData('taskId', taskId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, newPriority) => {
    e.preventDefault();
    const taskId = parseInt(e.dataTransfer.getData('taskId'));
    moveTask(taskId, newPriority);
  };

  // Handle tool actions from Mabel chat - uses extracted handler
  const onToolActions = async (toolActions) => {
    const token = await getToken();
    handleToolActions(toolActions, {
      // Task handlers
      addFullTask,
      updateTask,
      markDone,
      markUndone,
      toggleStar,
      deleteTask,
      clearAllTasks,
      // Inventory handlers
      addInventoryItem,
      updateInventoryItem,
      removeInventoryItem,
      markAsBought,
      markAsOut,
      // Meal plan handlers
      addMeal,
      removeMeal,
      skipMealDay,
      // Recipe handlers
      addRecipe,
      updateRecipe: updateUserRecipe,
      deleteRecipe: deleteUserRecipe,
      loadRecipes,
      // Filter handlers
      setStatusFilter: filters.setStatusFilter,
      setCapacity: filters.setCapacity,
      setShowStarredOnly: filters.setShowStarredOnly,
      // User state handler
      setUserState,
      // Toast
      showToast
    }, token);
  };

  const exportTasks = () => {
    const dataToExport = {
      version: '1.0.0',
      exportDate: new Date().toISOString(),
      tasks: tasks,
      capacity: filters.capacity,
      contextOptions: contextOptions
    };
    const dataStr = JSON.stringify(dataToExport, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'mabel-backup-' + new Date().toISOString().split('T')[0] + '.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImport = (replace = false) => {
    const result = importTasks(ui.importText, replace);
    if (result && result.success) {
      if (result.capacity) {
        filters.setCapacity(result.capacity);
      }
      ui.setImportText('');
      ui.setShowImportModal(false);
    }
  };

  // Handle navigation between views
  // Focus is now a sub-view of Plan, so translate 'focus' → plan with focus tab
  const handleNavigate = (mode) => {
    if (mode === 'focus') {
      try {
        localStorage.setItem('mabel-plan-subview', JSON.stringify('focus'));
      } catch {}
      ui.setViewMode('plan');
    } else {
      ui.setViewMode(mode);
    }
  };

  // Handle domain switching from sidebar
  const handleSelectDomain = (domainId) => {
    setCurrentDomain(domainId);
    // Reset to hub view mode when switching domains
    ui.setViewMode('hub');
  };

  // ============ RENDER HELPERS ============

  const renderTaskCard = (task) => {
    return (
      <TaskCard
        task={task}
        tasks={tasks}
        expandedTaskId={ui.expandedTaskId}
        setExpandedTaskId={ui.setExpandedTaskId}
        editBuffer={ui.editBuffer}
        setEditBuffer={ui.setEditBuffer}
        contextOptions={contextOptions}
        markDone={handleMarkDone}
        toggleStar={toggleStar}
        deleteTask={deleteTask}
        updateTask={updateTask}
        handleDragStart={handleDragStart}
      />
    );
  };

  // Clean up old session hint that may be stuck in localStorage
  useEffect(() => {
    localStorage.removeItem('kinde-session-active');
  }, []);

  // ============ RENDER ============

  // Show loading while Kinde checks auth
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: ABLE_COLORS.bg
      }}>
        <div style={{
          fontSize: '18px',
          color: ABLE_COLORS.textSecondary,
          fontWeight: '600'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  // Show landing page if not authenticated
  if (!isAuthenticated) {
    return <LandingPage />;
  }

  // Show splash screen on first visit
  if (ui.showSplash) {
    return <SplashScreen onClose={ui.handleCloseSplash} />;
  }

  // Calculate sidebar width for layout
  const sidebarWidth = widgetLayout.sidebarExpanded ? 180 : 56;

  return (
    <ErrorBoundary>
    <div style={{
      fontFamily: "'Inter', sans-serif",
      minHeight: '100vh',
      background: ABLE_COLORS.bg,
      display: 'flex',
    }}>
      {/* Sidebar Navigation */}
      <Sidebar
        enabledDomains={widgetLayout.enabledDomains}
        currentDomain={currentDomain}
        onSelectDomain={handleSelectDomain}
        expanded={widgetLayout.sidebarExpanded}
        pinned={widgetLayout.sidebarPinned}
        onToggleExpanded={widgetLayout.toggleSidebar}
        onSetExpanded={widgetLayout.setSidebarExpanded}
        onTogglePinned={() => widgetLayout.setSidebarPinned(!widgetLayout.sidebarPinned)}
        onOpenSettings={() => setShowWidgetSettings(true)}
        onOpenDomainSettings={() => setShowDomainSettings(true)}
        isMobile={isMobile}
      />

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        marginLeft: isMobile ? 0 : sidebarWidth,
        padding: '20px',
        paddingRight: ui.chatOpen && ui.isDesktop ? `${ui.chatWidth + 20}px` : '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        transition: ui.chatIsResizing ? 'none' : 'all 0.2s ease-out',
        minHeight: '100vh',
      }}>
      {/* Hub View - contains home dashboard and tasks view */}
      {ui.viewMode === 'hub' && (() => {
        const layoutForHub = widgetLayout.getLayout(currentDomain);
        return (
        <Hub
          currentDomain={currentDomain}
          onDomainChange={setCurrentDomain}
          user={user}
          tasks={tasks}
          filteredTasks={filteredTasks}
          currentTask={currentTask}
          focusIndex={focusIndex}
          currentState={userState}
          onStateChange={setUserState}
          onNavigate={handleNavigate}
          onOpenChat={() => ui.setChatOpen(true)}
          onOpenChatWithMessage={(msg) => {
            ui.setChatInitialMessage(msg);
            ui.setChatOpen(true);
          }}
          onOpenActivityLog={() => ui.setShowActivityLog(true)}
          onClearActivityLog={clearActivityLog}
          onAddTask={(title) => addTask(title)}
          onMarkDone={handleMarkDone}
          onMarkUndone={handleMarkUndone}
          onSkip={handleSkipTask}
          onUpdateTask={updateTask}
          onDeleteTask={deleteTask}
          onToggleStar={toggleStar}
          onClearAllTasks={clearAllTasks}
          onLogStateCheckin={logStateCheckin}
          pendingFactsCount={pendingFactsCount}
          pendingAliasesCount={pendingAliasesCount}
          onOpenReviewModal={() => ui.setShowReviewModal(true)}
          onReviewChanged={refreshReviewCounts}
          isSyncing={isSyncing}
          syncError={syncError}
          activityLogEntries={activityLogEntries}
          // Filter props
          capacity={filters.capacity}
          setCapacity={filters.setCapacity}
          statusFilter={filters.statusFilter}
          setStatusFilter={filters.setStatusFilter}
          showStarredOnly={filters.showStarredOnly}
          setShowStarredOnly={filters.setShowStarredOnly}
          expandedFilter={ui.expandedFilter}
          setExpandedFilter={ui.setExpandedFilter}
          contextOptions={contextOptions}
          // Kitchen inventory props
          stockedItems={stockedItems}
          shoppingList={shoppingList}
          boughtItems={boughtItems}
          inventoryCategories={inventoryCategories}
          onAddInventoryItem={addInventoryItem}
          onAddInventoryItems={addInventoryItems}
          onUpdateInventoryItem={updateInventoryItem}
          onRemoveInventoryItem={removeInventoryItem}
          onMarkAsOut={markAsOut}
          onAddToShoppingList={addToShoppingList}
          onMarkAsBought={markAsBought}
          onUndoBought={undoBought}
          // Meal plan props
          mealPlanMeals={mealPlanMeals}
          mealPlanWeekStart={mealPlanWeekStart}
          mealPlanLoading={mealPlanLoading}
          mealPlanSaving={mealPlanSaving}
          onAddMeal={addMeal}
          onRemoveMeal={removeMeal}
          onSkipMealDay={skipMealDay}
          onClearMealDay={clearMealDay}
          onSetMealPlanWeekStart={setMealPlanWeekStart}
          // Food log props
          foodLogDate={foodLogDate}
          foodLogEntries={foodLogEntries}
          entriesByMeal={entriesByMeal}
          foodLogTotals={foodLogTotals}
          foodLogLoading={foodLogLoading}
          foodLogSaving={foodLogSaving}
          foodLogIsToday={foodLogIsToday}
          trackedMacros={trackedMacros}
          dailyTargets={dailyTargets}
          availableMacros={availableMacros}
          onAddFoodEntry={addFoodEntry}
          onDeleteFoodEntry={deleteFoodEntry}
          onChangeFoodLogDate={changeFoodLogDate}
          onGoToFoodLogToday={goToFoodLogToday}
          onAddTrackedMacro={addTrackedMacro}
          onRemoveTrackedMacro={removeTrackedMacro}
          // Layout props
          isMobile={isMobile}
          onOpenSidebar={widgetLayout.toggleSidebar}
          widgetLayout={layoutForHub}
        />
        );
      })()}

      {/* Roadmap View - per-project launch progress */}
      {ui.viewMode === 'roadmap' && (
        <RoadmapView
          tasks={tasks}
          onTaskAction={updateTask}
          onNavigate={handleNavigate}
          getToken={getToken}
          isAuthenticated={isAuthenticated}
        />
      )}

      {/* Plan View - show header + plan/focus/gantt sub-views */}
      {ui.viewMode !== 'hub' && ui.viewMode !== 'roadmap' && (
        <div style={{
          width: '100%',
          maxWidth: '900px'
        }}>
          {/* Header */}
          <ViewHeader
            currentTime={ui.currentTime}
            tasks={tasks}
            pendingFactsCount={pendingFactsCount}
            pendingAliasesCount={pendingAliasesCount}
            showMenu={ui.showMenu}
            user={user}
            onNavigate={handleNavigate}
            onOpenActivityLog={() => ui.setShowActivityLog(true)}
            onOpenReviewModal={() => ui.setShowReviewModal(true)}
            onOpenChat={() => ui.setChatOpen(true)}
            onOpenMemorySettings={() => ui.setShowMemorySettings(true)}
            onToggleMenu={() => ui.setShowMenu(!ui.showMenu)}
          />

          {/* All Modals */}
          <Modals
            showMenu={ui.showMenu}
            setShowMenu={ui.setShowMenu}
            onExport={exportTasks}
            onOpenImport={() => ui.setShowImportModal(true)}
            showImportModal={ui.showImportModal}
            setShowImportModal={ui.setShowImportModal}
            importText={ui.importText}
            setImportText={ui.setImportText}
            onImport={handleImport}
            editingTask={ui.editingTask}
            setEditingTask={ui.setEditingTask}
            tasks={tasks}
            contextOptions={contextOptions}
            onUpdateTask={handleUpdateTask}
            onCloseEdit={() => ui.setEditingTask(null)}
          />

          {/* Context Filters */}
          <FilterPanel
            capacity={filters.capacity}
            setCapacity={filters.setCapacity}
            statusFilter={filters.statusFilter}
            setStatusFilter={filters.setStatusFilter}
            showStarredOnly={filters.showStarredOnly}
            setShowStarredOnly={filters.setShowStarredOnly}
            expandedFilter={ui.expandedFilter}
            setExpandedFilter={ui.setExpandedFilter}
            contextOptions={contextOptions}
            viewMode={ui.viewMode}
          />

          {/* Main Content - PlanView handles Cards/Gantt/Focus sub-views */}
          <PlanView
            filteredTasks={filteredTasks}
            capacity={filters.capacity}
            newTaskTitle={ui.newTaskTitle}
            setNewTaskTitle={ui.setNewTaskTitle}
            onAddTask={handleAddTask}
            renderTaskCard={renderTaskCard}
            handleDragOver={handleDragOver}
            handleDrop={handleDrop}
            currentTask={currentTask}
            focusIndex={focusIndex}
            isCardFlipped={ui.isCardFlipped}
            setIsCardFlipped={ui.setIsCardFlipped}
            onSkip={handleSkipTask}
            onMarkDone={handleMarkDone}
          />
        </div>
      )}
      </div>
      {/* End Main Content Area */}

      {/* Widget Settings Modal */}
      {showWidgetSettings && (
        <WidgetSettings
          domain={currentDomain}
          currentLayout={widgetLayout.getLayout(currentDomain)}
          onUpdateLayout={(layout) => widgetLayout.setLayout(currentDomain, layout)}
          onResetLayout={() => widgetLayout.resetLayout(currentDomain)}
          onClose={() => setShowWidgetSettings(false)}
        />
      )}

      {/* Domain Settings Modal */}
      {showDomainSettings && (
        <DomainSettings
          enabledDomains={widgetLayout.enabledDomains}
          onEnableDomain={widgetLayout.enableDomain}
          onDisableDomain={widgetLayout.disableDomain}
          onReorderDomains={widgetLayout.reorderDomains}
          onClose={() => setShowDomainSettings(false)}
        />
      )}

      {/* Memory Settings Modal */}
      <MemorySettings
        isOpen={ui.showMemorySettings}
        onClose={() => ui.setShowMemorySettings(false)}
      />

      {/* Review Modal (Facts + Aliases) */}
      <ReviewModal
        isOpen={ui.showReviewModal}
        onClose={() => ui.setShowReviewModal(false)}
        onReviewChanged={refreshReviewCounts}
      />

      {/* Consent Dialog (first use) */}
      <ConsentDialog
        isOpen={ui.showConsentDialog}
        onAccept={() => ui.setShowConsentDialog(false)}
      />

      {/* AI Chat Panel */}
      <ChatPanel
        isOpen={ui.chatOpen}
        onClose={() => {
          ui.setChatOpen(false);
          ui.setChatInitialMessage(null);
        }}
        initialMessage={ui.chatInitialMessage}
        onInitialMessageSent={() => ui.setChatInitialMessage(null)}
        tasks={tasks}
        filteredTasks={filteredTasks}
        capacity={filters.capacity}
        viewMode={ui.viewMode}
        currentTask={currentTask}
        statusFilter={filters.statusFilter}
        onMessageSent={refreshReviewCounts}
        onToolActions={onToolActions}
        messages={chatMessages}
        setMessages={setChatMessages}
        stockedItems={stockedItems}
        shoppingList={shoppingList}
        recipes={recipes}
        userState={userState}
        // Chat panel sizing
        isDesktop={ui.isDesktop}
        chatWidth={ui.chatWidth}
        chatIsResizing={ui.chatIsResizing}
        chatShowResizeHint={ui.chatShowResizeHint}
        chatStartResize={ui.chatStartResize}
      />

      {/* Timeline Modal (replaces old Activity Log) */}
      <TimelineView
        isOpen={ui.showActivityLog}
        onClose={() => ui.setShowActivityLog(false)}
        entries={activityLogEntries}
      />
    </div>
    </ErrorBoundary>
  );
}
